import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="web-push"
export default class extends Controller {
  static values = {
    vapidKeyUrl: { type: String, default: "/u/web_push_subscription/vapid_public_key" },
    subscriptionUrl: { type: String, default: "/u/web_push_subscription" }
  }

  static targets = ["button", "label", "iconEnabled", "iconDisabled"]

  connect() {
    this.checkSupport()
    this.listenForNavigation()
  }

  listenForNavigation() {
    if (!('serviceWorker' in navigator)) return

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'navigate' && event.data?.url) {
        window.location.href = event.data.url
      }
    })
  }

  async checkSupport() {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      this.hideButton()
      return
    }

    try {
      const registration = await navigator.serviceWorker.register('/service-worker.js')
      this.registration = registration

      // Check if already subscribed
      const subscription = await registration.pushManager.getSubscription()
      this.updateUI(!!subscription)
    } catch (error) {
    }
  }

  async toggle() {
    if (!this.registration) {
      return
    }

    const subscription = await this.registration.pushManager.getSubscription()
    if (subscription) {
      await this.unsubscribe()
    } else {
      await this.requestPermission()
    }
  }

  async requestPermission() {
    const permission = await Notification.requestPermission()
    if (permission !== 'granted') {
      return
    }

    await this.subscribe()
  }

  async subscribe() {
    try {
      // Get VAPID public key
      const response = await fetch(this.vapidKeyUrlValue)
      const { vapid_public_key } = await response.json()

      if (!vapid_public_key) {
          return
      }

      // Convert VAPID key to Uint8Array
      const applicationServerKey = this.urlBase64ToUint8Array(vapid_public_key)

      // Subscribe to push
      const subscription = await this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      })

      // Send subscription to server
      await fetch(this.subscriptionUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      })

      this.updateUI(true)
    } catch (error) {
    }
  }

  async unsubscribe() {
    try {
      const subscription = await this.registration.pushManager.getSubscription()
      if (!subscription) return

      await subscription.unsubscribe()

      // Notify server
      await fetch(this.subscriptionUrlValue, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ subscription: subscription.toJSON() })
      })

      this.updateUI(false)
    } catch (error) {
    }
  }

  updateUI(subscribed) {
    if (this.hasButtonTarget) {
      if (subscribed) {
        this.buttonTarget.title = "Notifications enabled (click to disable)"
        this.buttonTarget.classList.remove("text-stone-400", "dark:text-slate-400")
        this.buttonTarget.classList.add("text-teal-500", "dark:text-teal-400")
      } else {
        this.buttonTarget.title = "Enable desktop notifications"
        this.buttonTarget.classList.remove("text-teal-500", "dark:text-teal-400")
        this.buttonTarget.classList.add("text-stone-400", "dark:text-slate-400")
      }
    }

    if (this.hasIconEnabledTarget && this.hasIconDisabledTarget) {
      this.iconEnabledTarget.classList.toggle("hidden", !subscribed)
      this.iconDisabledTarget.classList.toggle("hidden", subscribed)
    }

    if (this.hasLabelTarget) {
      this.labelTarget.textContent = subscribed ? "On" : "Off"
    }
  }

  hideButton() {
    if (this.hasButtonTarget) {
      this.buttonTarget.style.display = 'none'
    }
  }

  // Helper to convert base64 to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4)
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/')

    const rawData = window.atob(base64)
    const outputArray = new Uint8Array(rawData.length)

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i)
    }
    return outputArray
  }
}
